/**
 * The editable half of a deal, as the screen holds it (context.md §131).
 *
 * The deal screen saves once, from a button, rather than on every keystroke: somebody adjusting a
 * price and a close date at the same time means one change, and three PATCHes for it is three
 * chances for the middle one to lose. So the screen keeps a draft, and this module is what turns a
 * record into a draft, a draft back into a payload, and — the part worth testing — a typed amount
 * into centavos.
 *
 * Money is the reason this is a module rather than four lines inside the component. `1.500,00` and
 * `1500.50` are both things people type into the same field, and reading either one wrong is a
 * deal that silently changes value by a factor of a hundred.
 */

import type { DealView } from './detail-api';

export interface DealForm {
  /** As typed, not as parsed: re-formatting under somebody's cursor is how a field fights back. */
  amount: string;
  expected: string;
  priority: DealView['priority'];
  temperature: string;
  sourceId: string;
  ownerId: string;
}

export function formOf(record: DealView): DealForm {
  return {
    amount: centsToText(record.amount_cents),
    expected: record.expected_close_date ?? '',
    priority: record.priority,
    temperature: record.temperature ?? '',
    sourceId: record.source_id ?? '',
    ownerId: record.owner_id ?? '',
  };
}

/** Centavos as somebody would type them here: comma for the decimal, no thousands separator. */
export function centsToText(cents: number): string {
  return (cents / 100).toFixed(2).replace('.', ',');
}

/**
 * What somebody typed, as centavos.
 *
 * A comma means the text is written the Brazilian way, so the dots in it are thousands separators
 * and go. Without a comma, a single dot is the decimal point — somebody typing `1500.50` on a
 * numeric keypad means fifteen hundred and fifty centavos, not a hundred and fifty thousand reais.
 */
export function textToCents(text: string): number {
  const cleaned = text.replace(/[^\d.,-]/g, '');
  const normalised = cleaned.includes(',') ? cleaned.replace(/\./g, '').replace(',', '.') : cleaned;
  const value = Number(normalised);

  if (!Number.isFinite(value) || value < 0) return 0;

  return Math.round(value * 100);
}

/**
 * Only what actually moved.
 *
 * Sending the whole form would overwrite a field somebody else changed while this screen was open,
 * with a value this screen read before they did. A PATCH of two keys can only lose the two keys it
 * names.
 */
export function changesBetween(base: DealForm, next: DealForm): Record<string, unknown> {
  const changes: Record<string, unknown> = {};

  // Compared as centavos, so `1500,00` and `1500.00` are the same edit and neither is one.
  if (textToCents(next.amount) !== textToCents(base.amount)) {
    changes.amountCents = textToCents(next.amount);
  }

  if (next.expected !== base.expected) changes.expectedCloseDate = next.expected || null;
  if (next.priority !== base.priority) changes.priority = next.priority;
  if (next.temperature !== base.temperature) changes.temperature = next.temperature || null;
  if (next.sourceId !== base.sourceId) changes.sourceId = next.sourceId || null;
  if (next.ownerId !== base.ownerId) changes.ownerId = next.ownerId || null;

  return changes;
}

/**
 * The chosen day, as the instant the server wants.
 *
 * Midday rather than midnight: the date comes from a `<date>` field, which has no time in it, and
 * midnight local is the previous day in UTC for everybody west of Greenwich — which is all of
 * Brazil. Noon is far enough from either edge that the day survives the round trip back.
 */
export function dueAt(date: string): string {
  return new Date(`${date}T12:00:00`).toISOString();
}
